import web3 from 'web3';

import { getBnbPrice } from '@/services/farming';
import ERC20_ABI from '@/utils/abi/erc20.json';
import KWT_FARMING_LOCK_ABI from '@/utils/abi/kwt-lock-farming.json';
import PANCAKE_FACTORY_ABI from '@/utils/abi/pancake-factory.json';
import PANCAKE_PAIR_ABI from '@/utils/abi/pancake-pair.json';
import { CHAIN_ID, read } from '@/utils/web3Service';

import type { DataGraphHistory } from '../page';
import {
  KWT_ADDRESS,
  KWT_FARMING_LOCK_2_ADDRESS,
  PANCAKE_FACTORY_ADDRESS,
  POOL_ADDRESS,
  WBNB_ADDRESS,
  ZERO_ADDRESS,
} from './constants';

export const getAprParams = (): string => {
  let params = '';
  Object.keys(POOL_ADDRESS).forEach(
    // @ts-ignore
    (i, idx) => (params += `${idx === 0 ? '' : '&'}addresses[${idx}]=${POOL_ADDRESS[i]}`),
  );

  return params;
};

export const convertGraphData = (graph: DataGraphHistory[]) => {
  const finalGraphData = [];
  if (graph.length > 0) {
    let graphDataLength = 0;
    for (let j = 0; j < graph.length; j += 1) {
      const graphLength = graph[j]?.data?.length || 0;
      if (graphLength > graphDataLength) {
        graphDataLength = graphLength;
      }
    }
    for (let i = 0; i < graphDataLength; i += 1) {
      let datum = 0;
      for (let j = 0; j < graph.length; j += 1) {
        const graphLength = graph[j]?.data?.length || 0;
        if (graphLength > i) {
          const graphDt = graph[j]?.data[i]?.[1] || 0;
          datum += graphDt;
        }
      }
      const graphDt = graph[0]?.data[i]?.[0] || 0;
      finalGraphData.push([graphDt * 1000, datum]);
    }
  }

  return finalGraphData;
};

export const getPairPrice = async (
  pancakePairAddr: string,
  bnbPrice: number,
  totalLiquidity: number,
) => {
  try {
    const totalSupply = await read('totalSupply', pancakePairAddr, PANCAKE_PAIR_ABI as any, []);
    const reserves = await read('getReserves', pancakePairAddr, PANCAKE_PAIR_ABI as any, []);
    const token0 = await read('token0', pancakePairAddr, PANCAKE_PAIR_ABI as any, []);
    const token1 = await read('token1', pancakePairAddr, PANCAKE_PAIR_ABI as any, []);
    if (token0.toLowerCase() === WBNB_ADDRESS.toLowerCase()) {
      return ((reserves[0] / 10 ** 18) * bnbPrice * 2 * totalLiquidity) / totalSupply;
    }
    if (token1.toLowerCase() === WBNB_ADDRESS.toLowerCase()) {
      return ((reserves[1] / 10 ** 18) * bnbPrice * 2 * totalLiquidity) / totalSupply;
    }
    return 0;
  } catch (err) {
    return 0;
  }
};

export const getTokenPrice = async (tokenAddr: string, amount: number) => {
  try {
    const pairWithBNB = await read('getPair', PANCAKE_FACTORY_ADDRESS, PANCAKE_FACTORY_ABI as any, [
      WBNB_ADDRESS,
      tokenAddr,
    ]);
    if (pairWithBNB === ZERO_ADDRESS) return 0;
    const bnbPrice = await getBnbPrice();
    const reserves = await read('getReserves', pairWithBNB, PANCAKE_PAIR_ABI as any, []);
    const token0: string = await read('token0', pairWithBNB, PANCAKE_PAIR_ABI as any, []);
    if (token0.toLowerCase() === WBNB_ADDRESS)
      return (
        (parseFloat(reserves['0']) / parseFloat(reserves['1'])) *
          (bnbPrice.data?.wbnb?.usd || 0) *
          amount || 0
      );
    return (
      (parseFloat(reserves['1']) / parseFloat(reserves['0'])) *
        (bnbPrice.data?.wbnb?.usd || 0) *
        amount || 0
    );
  } catch (err) {
    return 0;
  }
};

export const getFarmingLockData = async (address: string, account: string | null | undefined) => {
  let epochNow = 0;
  const [userDatas, epochConfigs, totalLiquidity, lastEpochPending] = await Promise.all([
    read('userDatas', address, KWT_FARMING_LOCK_ABI as any, [account], undefined, CHAIN_ID),
    read('epochNow', address, KWT_FARMING_LOCK_ABI as any, [], undefined, CHAIN_ID).then((res) => {
      epochNow = +res;
      return read('epochConfigs', address, KWT_FARMING_LOCK_ABI as any, [res], undefined, CHAIN_ID);
    }),
    read('totalLock', address, KWT_FARMING_LOCK_ABI as any, [], undefined, CHAIN_ID),
    read('pending', address, KWT_FARMING_LOCK_ABI as any, [account], undefined, CHAIN_ID),
  ]);

  const rewardTokenSymbol = 'KWT';
  const name = 'KWT';
  const stakingToken = KWT_ADDRESS;
  const rewardPerSecond = +web3.utils.fromWei(epochConfigs.rewardPerSecond) * 10 ** 6;

  const decimals = 18;
  const tokenBalance = await read(
    'balanceOf',
    stakingToken,
    ERC20_ABI as any,
    [String(account)],
    undefined,
    CHAIN_ID,
  );
  const allowance = await read(
    'allowance',
    stakingToken,
    ERC20_ABI as any,
    [account, address],
    undefined,
    CHAIN_ID,
  );
  const gTokenPrice = await getTokenPrice(stakingToken, +web3.utils.fromWei(totalLiquidity));

  const apr = rewardPerSecond * 24 * 3600 * 365;

  let earned = +web3.utils.fromWei(userDatas.rewardPending);
  if (userDatas.epoch < epochNow) earned += +web3.utils.fromWei(lastEpochPending[0]);

  return {
    vaultAddress: address,
    tokenAddress: stakingToken,
    tokenBalance: web3.utils.fromWei(tokenBalance),
    creatorAddress: 'hung',
    name,
    staked: web3.utils.fromWei(userDatas.amount),
    earned,
    pendingReward: earned,
    liquidity: web3.utils.fromWei(totalLiquidity),
    allowance,
    decimals,
    rewardPerSecond,
    rewardToken: stakingToken,
    gTokenPrice,
    rewardTokenSymbol,
    startLocking: +epochConfigs.start,
    lastRewardBlock: +epochConfigs.end,
    apr,
    description: address === KWT_FARMING_LOCK_2_ADDRESS ? '(Lock 4 months)' : '(Lock 6 months)',
    userEpoch: +userDatas.epoch,
    epochNow,
    lastEpochPending: +web3.utils.fromWei(lastEpochPending[0]),
  };
};

export const getFarmingLockDataNoUser = async (address: string) => {
  let epochNow;
  const [epochConfigs, totalLiquidity] = await Promise.all([
    read('epochNow', address, KWT_FARMING_LOCK_ABI as any, [], undefined, CHAIN_ID).then((res) => {
      epochNow = +res;
      return read('epochConfigs', address, KWT_FARMING_LOCK_ABI as any, [res], undefined, CHAIN_ID);
    }),
    read('totalLock', address, KWT_FARMING_LOCK_ABI as any, [], undefined, CHAIN_ID),
  ]);

  const rewardTokenSymbol = 'KWT';
  const name = 'KWT';
  const stakingToken = KWT_ADDRESS;
  const rewardPerSecond = +web3.utils.fromWei(epochConfigs.rewardPerSecond) * 10 ** 6;

  const decimals = 18;
  const tokenBalance = 0;
  const allowance = 0;
  const gTokenPrice = await getTokenPrice(stakingToken, +web3.utils.fromWei(totalLiquidity));
  const apr = rewardPerSecond * 24 * 3600 * 365;
  const earned = 0;

  return {
    vaultAddress: address,
    tokenAddress: stakingToken,
    tokenBalance,
    creatorAddress: 'hung',
    name,
    staked: 0,
    earned,
    pendingReward: earned,
    liquidity: web3.utils.fromWei(totalLiquidity),
    allowance,
    decimals,
    rewardPerSecond,
    rewardToken: stakingToken,
    gTokenPrice,
    rewardTokenSymbol,
    startLocking: +epochConfigs.start,
    lastRewardBlock: +epochConfigs.end,
    apr,
    description: address === KWT_FARMING_LOCK_2_ADDRESS ? '(Lock 4 months)' : '(Lock 6 months)',
    userEpoch: 0,
    epochNow,
    lastEpochPending: 0,
  };
};

export const estimatePendingReward = (reward: any, setReward: any, timeout: any) => {
  const estimateRewards: any = {};
  Object.keys(reward).forEach((poolAddress) => {
    const blockNow = reward[poolAddress].blockNow + 1;
    const {
      lastRewardBlock,
      aiRightPerBlock,
      accPerShare,
      liquidity,
      rewardDebt,
      pendingRewards,
      amount,
    } = reward[poolAddress];
    const aiRight = (blockNow - lastRewardBlock) * aiRightPerBlock;
    const accPerShareView = parseInt(accPerShare) + (aiRight * 1e12) / liquidity;
    const estimateReward =
      (amount * accPerShareView) / 1e12 - parseInt(rewardDebt) + parseInt(pendingRewards);
    estimateRewards[poolAddress] = {
      ...reward[poolAddress],
      reward: estimateReward,
      blockNow,
    };
  });
  setReward(estimateRewards);
  timeout.current = setTimeout(
    () => estimatePendingReward(estimateRewards, setReward, timeout),
    3000,
  );
};

export const formatNumberFarming = (x: number) => {
  const round = Math.floor(x * 100) / 100;
  const addComma = round.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (addComma.indexOf('.') === -1) return `${addComma}.00`;
  return addComma;
};
